import { createRouter, createWebHistory } from 'vue-router'
import { getActivePinia } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { getSuperAdminBootstrapRedirect } from '@/router/superAdminBootstrap'
import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    redirect: () => {
      // This will be handled by the navigation guard
      return '/login'
    }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/join/:token',
    name: 'join',
    component: () => import('@/views/parent/OnboardingWizardView.vue'),
    meta: { title: 'Join school' }
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/views/parent/OnboardingWizardView.vue'),
    meta: { title: 'Parent onboarding' }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/legal/PrivacyPolicyView.vue'),
    meta: { title: 'Privacy Policy' }
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/legal/TermsOfServiceView.vue'),
    meta: { title: 'Terms of Service' }
  },
  {
    path: '/sms-test',
    name: 'sms-test',
    component: () => import('@/views/SmsTestView.vue'),
    meta: { requiresAuth: true, requiresRole: ['admin', 'super_admin'], title: 'SMS Test' }
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true, requiresRole: ['admin', 'super_admin'] },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard'
      },
      {
        path: 'dashboard',
        name: 'admin-dashboard',
        component: () => import('@/views/admin/DashboardView.vue'),
        meta: { title: 'Dashboard' }
      },
      {
        path: 'setup',
        name: 'admin-setup',
        component: () => import('@/views/admin/SetupView.vue'),
        meta: { title: 'Setup' },
        children: [
          {
            path: '',
            redirect: '/admin/setup/users'
          },
          {
            path: 'users',
            name: 'admin-users',
            component: () => import('@/views/admin/UsersView.vue'),
            meta: { title: 'Users Management' }
          },
          {
            path: 'buses',
            name: 'admin-buses',
            component: () => import('@/views/admin/BusesView.vue'),
            meta: { title: 'Buses Management' }
          },
          {
            path: 'routes',
            name: 'admin-routes',
            component: () => import('@/views/admin/RoutesView.vue'),
            meta: { title: 'Routes Management' }
          },
          {
            path: 'route-debug',
            name: 'admin-route-debug',
            component: () => import('@/views/admin/RouteDebugView.vue'),
            meta: { title: 'Route Debug' }
          },
          {
            path: 'students',
            name: 'admin-students',
            component: () => import('@/views/admin/StudentsView.vue'),
            meta: { title: 'Students Management' }
          },
          {
            path: 'journeys',
            name: 'admin-journeys',
            component: () => import('@/views/admin/JourneysView.vue'),
            meta: { title: 'Journeys' }
          },
          {
            path: 'settings',
            name: 'admin-settings',
            component: () => import('@/views/admin/SettingsView.vue'),
            meta: { title: 'Settings' }
          }
        ]
      },
      {
        path: 'monitoring',
        name: 'admin-monitoring',
        component: () => import('@/views/admin/MonitoringView.vue'),
        meta: { requiresRole: ['super_admin'], title: 'System Monitoring' }
      },
      {
        path: 'analytics',
        name: 'admin-analytics',
        component: () => import('@/views/admin/DashboardView.vue'),
        meta: { requiresRole: ['super_admin'], title: 'Analytics' }
      },
      {
        path: 'history',
        name: 'admin-history',
        component: () => import('@/views/admin/HistoryView.vue'),
        meta: { title: 'Journey History' }
      },
      {
        path: 'profile',
        name: 'admin-profile',
        component: () => import('@/views/admin/ProfileView.vue'),
        meta: { title: 'My Profile' }
      },
      {
        path: 'dev-tools',
        name: 'admin-dev-tools',
        component: () => import('@/views/admin/DevToolsView.vue'),
        meta: { title: 'Development Tools' }
      }
    ]
  },
  {
    path: '/driver',
    name: 'driver',
    component: () => import('@/views/driver/DriverLayout.vue'),
    meta: { requiresAuth: true, requiresRole: ['driver'] },
    children: [
      {
        path: '',
        name: 'driver-home',
        component: () => import('@/views/driver/DriverHomeView.vue'),
        redirect: { name: 'driver-trips' },
        children: [
          {
            path: '',
            name: 'driver-trips',
            component: () => import('@/views/driver/BusesView.vue'),
            meta: { title: 'Trips' }
          },
          {
            path: 'active-journey',
            name: 'driver-active-journey',
            component: () => import('@/views/driver/ActiveJourneyView.vue'),
            meta: { title: 'Active Journey' }
          },
          {
            path: 'history',
            name: 'driver-history',
            component: () => import('@/views/driver/HistoryView.vue'),
            meta: { title: 'History' }
          }
        ]
      },
      {
        path: 'profile',
        name: 'driver-profile',
        component: () => import('@/views/driver/ProfileView.vue'),
        meta: { title: 'Profile' }
      }
    ]
  },
  {
    path: '/parent',
    name: 'parent',
    component: () => import('@/views/parent/ParentLayout.vue'),
    meta: { requiresAuth: true, requiresRole: ['parent'] },
    children: [
      {
        path: '',
        name: 'parent-home',
        component: () => import('@/views/parent/ParentHomeView.vue'),
        redirect: { name: 'parent-tracking' },
        children: [
          {
            path: 'track',
            name: 'parent-tracking',
            component: () => import('@/views/parent/TrackingView.vue'),
            meta: { title: 'Track' }
          },
          {
            path: 'children',
            name: 'parent-children',
            component: () => import('@/views/parent/ChildrenView.vue'),
            meta: { title: 'Children' }
          },
          {
            path: 'history',
            name: 'parent-history',
            component: () => import('@/views/parent/HistoryView.vue'),
            meta: { title: 'History' }
          },
          {
            path: 'notifications',
            name: 'parent-notifications',
            component: () => import('@/views/parent/NotificationsView.vue'),
            meta: { title: 'Notifications' }
          }
        ]
      },
      {
        path: 'payment/complete',
        name: 'parent-payment-complete',
        component: () => import('@/views/parent/PaymentCompleteView.vue'),
        meta: { title: 'Complete Payment' }
      },
      {
        path: 'profile',
        name: 'parent-profile',
        component: () => import('@/views/parent/ProfileView.vue'),
        meta: { title: 'Profile' }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  try {
    // Check if Pinia is initialized
    if (!getActivePinia()) {
      // Pinia not ready yet, allow navigation
      next()
      return
    }

    const authStore = useAuthStore()
    
    // Handle home page - always redirect to login if not authenticated, dashboard if authenticated
    if (to.path === '/') {
      if (authStore.user && authStore.isAuthenticated) {
        // Redirect to appropriate dashboard based on role
        if (authStore.hasRole('driver')) {
          next('/driver')
          return
        } else if (authStore.hasRole('parent')) {
          next('/parent')
          return
        } else if (authStore.hasAnyRole(['admin', 'super_admin'])) {
          next('/admin')
          return
        }
      }
      // Not authenticated, redirect to login
      next('/login')
      return
    }
  
  // Handle login page — allow return to onboarding/join after auth
  if (to.name === 'login' || to.path === '/login') {
    // If user is already authenticated, redirect to their dashboard
    // unless they came from onboarding (preserve wizard)
    if (authStore.user && authStore.isAuthenticated) {
      const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : ''
      if (redirect.startsWith('/onboarding') || redirect.startsWith('/join/')) {
        next(redirect)
        return
      }
      if (authStore.hasRole('driver')) {
        next('/driver')
      } else if (authStore.hasRole('parent')) {
        next('/parent')
      } else if (authStore.hasAnyRole(['admin', 'super_admin'])) {
        next('/admin')
      } else {
        next('/')
      }
      return
    }
    next()
    return
  }

  // Linking another child requires an authenticated parent
  if (to.name === 'onboarding' && to.query.link === '1') {
    if (!authStore.user || !authStore.isAuthenticated) {
      const hasSession = await authStore.checkSession()
      if (!hasSession || !authStore.user) {
        next({ name: 'login', query: { redirect: to.fullPath } })
        return
      }
    }
    if (!authStore.hasRole('parent')) {
      next('/parent')
      return
    }
    next()
    return
  }
  
  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    // If user exists in store, trust it and allow access
    if (authStore.user && authStore.isAuthenticated) {
      // Check role requirements
      if (to.meta.requiresRole) {
        const requiredRoles = Array.isArray(to.meta.requiresRole) 
          ? to.meta.requiresRole 
          : [to.meta.requiresRole]
        
        if (!authStore.hasAnyRole(requiredRoles)) {
          // Redirect to appropriate dashboard based on user role
          if (authStore.hasRole('driver')) {
            next('/driver')
          } else if (authStore.hasRole('parent')) {
            next('/parent')
          } else if (authStore.hasAnyRole(['admin', 'super_admin'])) {
            next('/admin')
          } else {
            next('/login')
          }
          return
        }
      }
      const bootstrapRedirect = await getSuperAdminBootstrapRedirect(to)
      if (bootstrapRedirect) {
        next(bootstrapRedirect)
        return
      }
      // User is authenticated and has correct role, allow access
      next()
      return
    }
    
    // No user in store, check session
    try {
      const hasSession = await authStore.checkSession()
      if (!hasSession || !authStore.user) {
        next({ name: 'login', query: { redirect: to.fullPath } })
        return
      }
      
      // Check role requirements after session check
      if (to.meta.requiresRole) {
        const requiredRoles = Array.isArray(to.meta.requiresRole) 
          ? to.meta.requiresRole 
          : [to.meta.requiresRole]
        
        if (!authStore.hasAnyRole(requiredRoles)) {
          if (authStore.hasRole('driver')) {
            next('/driver')
          } else if (authStore.hasRole('parent')) {
            next('/parent')
          } else if (authStore.hasAnyRole(['admin', 'super_admin'])) {
            next('/admin')
          } else {
            next('/login')
          }
          return
        }
      }

      const bootstrapRedirect = await getSuperAdminBootstrapRedirect(to)
      if (bootstrapRedirect) {
        next(bootstrapRedirect)
        return
      }
      
      next()
    } catch (err) {
      console.error('Session check error:', err)
      next({ name: 'login', query: { redirect: to.fullPath } })
    }
    return
  }
  
  // Check if route requires guest (not logged in)
  if (to.meta.requiresGuest && authStore.user && authStore.isAuthenticated) {
    if (authStore.hasRole('driver')) {
      next('/driver')
    } else if (authStore.hasRole('parent')) {
      next('/parent')
    } else if (authStore.hasAnyRole(['admin', 'super_admin'])) {
      next('/admin')
    } else {
      next('/')
    }
    return
  }
  
  next()
  } catch (error) {
    console.error('Router error:', error)
    // Allow navigation to continue on error
    next()
  }
})

// Recover from stale chunk references after a deploy: each build hashes JS chunk filenames
// (e.g. MonitoringView-D4TbxbXe.js), and the server deletes old ones once a new build lands.
// A tab left open across a deploy still has the OLD hashes baked into its already-loaded
// router, so navigating to a lazy route fetches a file that no longer exists and Vite's
// dynamic import() rejects with "Failed to fetch dynamically imported module". In-app
// navigation can't recover from that — force one full reload to pick up the current
// index.html (served with no-cache) + correct chunk manifest, then land on the same route.
const CHUNK_RELOAD_KEY = 'sbt-chunk-reload-attempted'

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /failed to fetch dynamically imported module/i.test(message)
    || /error loading dynamically imported module/i.test(message)
    || /importing a module script failed/i.test(message)
}

router.onError((error, to) => {
  if (!isChunkLoadError(error)) return

  // Guard against a reload loop if the chunk keeps failing for some other reason
  // (e.g. the asset really is missing/broken, not just a stale reference).
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === to.fullPath) {
    console.error('Chunk still failing to load after a reload — not retrying again:', error)
    return
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, to.fullPath)
  window.location.href = to.fullPath
})

router.afterEach(() => {
  // Navigation actually succeeded — clear the guard so a future deploy can trigger recovery again.
  sessionStorage.removeItem(CHUNK_RELOAD_KEY)
})

export default router

